import React, { useState, useEffect, useMemo } from 'react';
import { X, UserCheck, Save, Mail, Shield, Activity } from 'lucide-react';
import { Button, Badge } from '../ui';
import {
  userService,
  User,
  UpdateUserRequest
} from '../../services/userService';
import { authService, Role } from '../../services/authService';
import { useAuthStore } from '../../stores/authStore';
import { getFilteredRolesForDisplay } from '../../utils/roleHelpers';

interface UserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  onSaved: () => void;
}

const UserDrawer: React.FC<UserDrawerProps> = ({
  isOpen,
  onClose,
  user,
  onSaved
}) => {
  const { user: currentUser } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    roleId: 1,
    statusId: 1,
    profileData: {} as Record<string, any>,
    preferences: {} as Record<string, any>,
  });

  // Track original data to detect changes
  const [originalData, setOriginalData] = useState({
    firstName: '',
    lastName: '',
    roleId: 1,
    statusId: 1,
  });

  // Filter roles based on current user's permissions
  const filteredRoles = useMemo(
    () => getFilteredRolesForDisplay(roles, currentUser),
    [roles, currentUser]
  );

  // Load roles when drawer opens
  useEffect(() => {
    const loadRoles = async () => {
      if (isOpen) {
        setLoadingRoles(true);
        try {
          const response = await authService.getRoles();
          if (response.success) {
            setRoles(response.data);
          }
        } catch (error) {
          console.error('Error loading roles:', error);
        } finally {
          setLoadingRoles(false);
        }
      }
    };

    if (roles.length === 0) {
      loadRoles();
    }
  }, [isOpen]);

  // Reset form when drawer opens/closes or user changes
  useEffect(() => {
    if (isOpen && user) {
      const initialData = {
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        roleId: user.roleId || 1,
        statusId: user.statusId || 1,
        profileData: user.profileData || {},
        preferences: user.preferences || {},
      };
      setFormData(initialData);
      setOriginalData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        roleId: user.roleId || 1,
        statusId: user.statusId || 1,
      });
      setError(null);
    }
  }, [isOpen, user]);

  // Check if form has been modified
  const hasChanges = useMemo(() => {
    return (
      formData.firstName !== originalData.firstName ||
      formData.lastName !== originalData.lastName ||
      formData.roleId !== originalData.roleId ||
      formData.statusId !== originalData.statusId
    );
  }, [formData, originalData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    if (!user) {
      setError('No user selected for editing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updateData: UpdateUserRequest = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        roleId: formData.roleId,
        statusId: formData.statusId,
        profileData: formData.profileData,
        preferences: formData.preferences,
      };
      
      const response = await userService.updateUser(user.id, updateData);
      
      if (response.success) {
        onSaved();
      } else {
        throw new Error(response.error?.message || 'Failed to update user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const statuses = [
    { id: 1, name: 'Active', description: 'User can log in and use the system' },
    { id: 3, name: 'Inactive', description: 'User account is inactive' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserCheck className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Edit User
                </h2>
                <p className="text-xs text-gray-600">
                  Update user information and permissions
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <form onSubmit={handleSubmit}>
              {/* Error Message */}
              {error && (
                <div className="mx-6 mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-xs">
                  {error}
                </div>
              )}

              {/* Account Information Section */}
              <div className="px-6 py-5">
                <div className="flex items-center mb-4">
                  <Mail className="h-4 w-4 text-gray-400 mr-2" />
                  <h3 className="text-sm font-semibold text-gray-900">Account Information</h3>
                </div>

                {/* Email Display (Read-only) */}
                <div className="bg-gray-50 rounded-lg p-4 mb-5">
                  <label className="block text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">
                    Email Address
                  </label>
                  <p className="text-sm text-gray-900 font-medium">{formData.email}</p>
                  <p className="mt-1 text-xs text-gray-500">
                    Email addresses cannot be changed
                  </p>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter first name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter last name"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200"></div>

              {/* Role and Permissions Section */}
              <div className="px-6 py-5">
                <div className="flex items-center mb-4">
                  <Shield className="h-4 w-4 text-gray-400 mr-2" />
                  <h3 className="text-sm font-semibold text-gray-900">Role & Permissions</h3>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      User Role
                    </label>
                    <select
                      value={formData.roleId}
                      onChange={(e) => handleInputChange('roleId', parseInt(e.target.value))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      disabled={loadingRoles}
                    >
                      {loadingRoles ? (
                        <option value="">Loading roles...</option>
                      ) : filteredRoles.length === 0 ? (
                        <option value="">No roles available</option>
                      ) : (
                        filteredRoles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.displayName}
                          </option>
                        ))
                      )}
                    </select>
                    <p className="mt-1.5 text-xs text-gray-500">
                      {filteredRoles.find(r => r.id === formData.roleId)?.roleDescription || 'Select a role to see description'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      Account Status
                    </label>
                    <select
                      value={formData.statusId}
                      onChange={(e) => handleInputChange('statusId', parseInt(e.target.value))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    >
                      {statuses.map(status => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1.5 text-xs text-gray-500">
                      {statuses.find(s => s.id === formData.statusId)?.description || ''}
                    </p>
                  </div>
                </div>

                {/* Current Status Badge */}
                <div className="mt-4 flex items-center space-x-2">
                  <Activity className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-xs text-gray-600">Current Status:</span>
                  <Badge variant={formData.statusId === 1 ? 'success' : 'error'}>
                    {statuses.find(s => s.id === formData.statusId)?.name || 'Unknown'}
                  </Badge>
                </div>
              </div>

            </form>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || !hasChanges}
              className="min-w-[120px]"
            >
              {loading ? (
                'Saving...'
              ) : (
                <>
                  <Save className="h-3.5 w-3.5 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDrawer;